// Диалоги и элементы ленты для таблиц: сетка вставки, «Вставить таблицу…», «Таблица из текста / CSV / Excel»,
// «Свойства таблицы» (головка, ширины граф в см, выравнивание граф).
import { useMemo, useRef, useState } from 'react';
import type { Ctx, DialogProps } from '../../../core/registry';
import { Btn, Field, Modal, Popover } from '../../../ui/controls';
import { Icon } from '../../../ui/icons';
import { PX_PER_CM, TEXT_W_CM, TEXT_W_PX } from '../util';
import {
  applyHeaderRows, applyWidths, cells, currentWidths, normalizeWidths, stoTableJson, tableCtx, textCell,
  type Align, type Matrix,
} from './model';
import { parseTextTable } from './parse';
import { defaultAligns } from './plugin';
import { insertTable, pasteIntoTable } from './insert';

// ------------------------------------------------------------------------------------------------ сетка в ленте
export function TableGrid({ ctx }: { ctx: Ctx }) {
  const ref = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState<[number, number]>([0, 0]);
  const R = 10; const C = 10;
  const insert = (r: number, c: number) => {
    setOpen(false);
    if (!ctx.editor) return;
    const m: Matrix = { rows: Array.from({ length: r }, () => Array.from({ length: c }, () => textCell(''))), headerRows: 1 };
    insertTable(ctx.editor, m);
  };
  return (
    <div ref={ref} className="ribbon-menu">
      <Btn icon="table" label="Таблица" large onClick={() => setOpen(!open)} testid="rb-table"
        title="Таблица по СТО: название над таблицей, головка, сетка 0,25 мм (4.9)"><Icon name="down" size={12} /></Btn>
      {open && (
        <Popover anchor={ref.current} onClose={() => setOpen(false)}>
          <div className="grid-picker tfc-grid-picker" onMouseLeave={() => setHover([0, 0])}>
            <div className="grid-picker-title">{hover[0] ? `Таблица: ${hover[0]} строк${hover[0] === 1 ? 'а' : hover[0] < 5 ? 'и' : ''} × ${hover[1]} граф${hover[1] === 1 ? 'а' : hover[1] < 5 ? 'ы' : ''}` : 'Строки × графы (1-я строка - головка)'}</div>
            {Array.from({ length: R }, (_, r) => (
              <div key={r} className="grid-row">
                {Array.from({ length: C }, (_, c) => (
                  <span key={c} className={'grid-cell' + (r < hover[0] && c < hover[1] ? ' is-on' : '') + (r === 0 && c < hover[1] && hover[0] ? ' is-head' : '')}
                    data-testid={`grid-${r + 1}x${c + 1}`}
                    onMouseEnter={() => setHover([r + 1, c + 1])} onClick={() => insert(r + 1, c + 1)} />
                ))}
              </div>
            ))}
            <div className="tfc-grid-actions">
              <button type="button" className="menu-item" data-testid="tfc-table-dialog" onMouseDown={(e) => e.preventDefault()}
                onClick={() => { setOpen(false); void ctx.run('table.insertDialog'); }}>
                <span className="menu-ico"><Icon name="table-props" size={15} /></span><span className="menu-label">Вставить таблицу…</span>
              </button>
              <button type="button" className="menu-item" onMouseDown={(e) => e.preventDefault()}
                onClick={() => { setOpen(false); void ctx.run('table.fromText'); }}>
                <span className="menu-ico"><Icon name="tfc-text-to-table" size={15} /></span><span className="menu-label">Из текста, CSV или Excel…</span>
              </button>
              <div className="tfc-grid-hint">Таблицу из Excel, Word или Google Таблиц можно просто вставить (Ctrl+V)</div>
            </div>
          </div>
        </Popover>
      )}
    </div>
  );
}

// ------------------------------------------------------------------------------------------------ «Вставить таблицу…»
export function InsertTableDialog({ ctx, close }: DialogProps) {
  const [rows, setRows] = useState(4);
  const [cols, setCols] = useState(3);
  const [head, setHead] = useState(1);
  const [caption, setCaption] = useState('');
  const ok = () => {
    if (!ctx.editor) return close();
    const r = Math.max(head + 1, Math.min(200, rows)); const c = Math.max(1, Math.min(30, cols));
    const m: Matrix = { rows: Array.from({ length: r }, () => Array.from({ length: c }, () => textCell(''))), headerRows: head };
    close(true);
    setTimeout(() => insertTable(ctx.editor!, m, caption.trim().replace(/\.$/, ''), !!caption.trim()), 0);
  };
  return (
    <Modal title="Вставить таблицу" testid="tfc-insert-table" onClose={() => close()} width={460}
      footer={<><Btn label="Отмена" onClick={() => close()} /><Btn label="Вставить" primary onClick={ok} testid="tfc-insert-ok" /></>}>
      <div className="form-grid" onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); ok(); } }}>
        <Field label="Строк (с головкой)"><input type="number" min={2} max={200} value={rows} onChange={(e) => setRows(+e.target.value || 2)} data-testid="tfc-rows" /></Field>
        <Field label="Граф"><input type="number" min={1} max={30} value={cols} onChange={(e) => setCols(+e.target.value || 1)} data-testid="tfc-cols" /></Field>
        <Field label="Строк головки" hint="головка обязательна (4.9.8); 2–3 строки - для многоуровневой головки">
          <select value={head} onChange={(e) => setHead(+e.target.value)}>{[1, 2, 3].map((n) => <option key={n} value={n}>{n}</option>)}</select>
        </Field>
        <Field label="Название таблицы" wide hint="с прописной буквы, без точки в конце (4.9.6); «Таблица N.» ставится сама">
          <input autoFocus value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Например: Параметры конфигурации узла" data-testid="tfc-caption" />
        </Field>
      </div>
      <p className="tfc-note">Графу «№ п/п» не делайте (4.9.13): номера при необходимости пишут в первой графе перед наименованием.</p>
    </Modal>
  );
}

// ------------------------------------------------------------------------------------------------ «Из текста, CSV, Excel»
const DELIMS: [string, string][] = [['', 'Автоматически'], ['\t', 'Табуляция (Excel)'], [';', 'Точка с запятой'], [',', 'Запятая (CSV)'], ['|', 'Вертикальная черта']];

export function TextToTableDialog({ ctx, props, close }: DialogProps) {
  const [text, setText] = useState<string>(props.text || '');
  const [delim, setDelim] = useState('');
  const [head, setHead] = useState(1);
  const [caption, setCaption] = useState('');
  const m = useMemo(() => (text.trim() ? parseTextTable(text, delim || undefined) : null), [text, delim]);
  const ok = () => {
    if (!m || !ctx.editor) return;
    close(true);
    const mm = { ...m, headerRows: head };
    setTimeout(() => {
      if (props.replace) {
        const { from, to } = props.replace as { from: number; to: number };
        ctx.editor!.chain().focus().deleteRange({ from, to }).run();
      }
      if (props.intoTable && tableCtx(ctx.editor!)) pasteIntoTable(ctx.editor!, mm);
      else insertTable(ctx.editor!, mm, caption.trim().replace(/\.$/, ''), !!caption.trim());
    }, 0);
  };
  const preview = m ? m.rows.slice(0, 8) : [];
  return (
    <Modal title="Таблица из текста, CSV или Excel" testid="tfc-text-table" onClose={() => close()} width={680}
      footer={<><span className="muted" style={{ marginRight: 'auto' }}>{m ? `${m.rows.length} × ${m.rows[0]?.length || 0}` : 'Вставьте данные'}</span>
        <Btn label="Отмена" onClick={() => close()} /><Btn label="Создать таблицу" primary disabled={!m} onClick={ok} testid="tfc-text-table-ok" /></>}>
      <Field label="Данные: каждая строка - строка таблицы; графы через табуляцию, «;», «,» или «|»" wide>
        <textarea className="tfc-textarea" autoFocus value={text} onChange={(e) => setText(e.target.value)} spellCheck={false}
          placeholder={'Параметр\tЗначение\nIP-адрес\t192.168.1.37'} data-testid="tfc-text-table-src" />
      </Field>
      <div className="form-grid">
        <Field label="Разделитель граф"><select value={delim} onChange={(e) => setDelim(e.target.value)}>{DELIMS.map(([v, t]) => <option key={t} value={v}>{t}</option>)}</select></Field>
        <Field label="Строк головки"><select value={head} onChange={(e) => setHead(+e.target.value)}>{[1, 2, 3].map((n) => <option key={n} value={n}>{n}</option>)}</select></Field>
        <Field label="Название таблицы" wide><input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Название (без точки в конце)" /></Field>
      </div>
      {preview.length > 0 && (
        <div className="tfc-preview">
          <table><tbody>{preview.map((r, i) => <tr key={i} className={i < head ? 'is-head' : ''}>{r.map((c, j) => <td key={j}>{cellText(c)}</td>)}</tr>)}</tbody></table>
          {m!.rows.length > preview.length && <div className="muted">… ещё строк: {m!.rows.length - preview.length}</div>}
        </div>
      )}
    </Modal>
  );
}

function cellText(c: { content: any[] }): string {
  return c.content.map((p) => (p.content || []).map((t: any) => t.text || '').join('')).join(' ');
}

// ------------------------------------------------------------------------------------------------ «Свойства таблицы»
export function TablePropsDialog({ ctx, close }: DialogProps) {
  const ed = ctx.editor!;
  const t0 = tableCtx(ed);
  const [head, setHead] = useState(t0?.headerRows || 1);
  const init = t0 ? currentWidths(t0) : [];
  const auto = !init.some((x) => x);
  const [mode, setMode] = useState<'auto' | 'manual'>(auto ? 'auto' : 'manual');
  const [cm, setCm] = useState<number[]>(() => {
    if (!t0) return [];
    const ws = auto ? measured(ed, t0) : normalizeWidths(init);
    return ws.map((w) => Math.round((w / PX_PER_CM) * 10) / 10);
  });
  const defs = t0 ? defaultAligns(t0) : [];
  const [aligns, setAligns] = useState<Align[]>(() => (t0 ? Array.from({ length: t0.map.width }, (_, c) => colAlign(t0, c)) : []));
  if (!t0) return <Modal title="Свойства таблицы" onClose={() => close()}><p>Поставьте курсор в таблицу.</p></Modal>;
  const sum = cm.reduce((a, b) => a + (b || 0), 0);
  const ok = () => {
    const t = tableCtx(ed);
    if (!t) return close();
    const tr = ed.state.tr;
    applyHeaderRows(tr, t, head);
    applyWidths(tr, t, mode === 'auto' ? null : normalizeWidths(cm.map((x) => Math.max(0.5, x || 0.5) * PX_PER_CM)));
    for (const c of cells(t)) {
      if (c.row < head) continue;
      const al = aligns[c.col] ?? null;
      if ((c.node.attrs.align ?? null) !== al) tr.setNodeMarkup(tr.mapping.map(c.pos), undefined, { ...tr.doc.nodeAt(tr.mapping.map(c.pos))!.attrs, align: al });
    }
    ed.view.dispatch(tr);
    close(true);
  };
  // вправо значения граф не выравнивают (4.9.23: значения - по центру графы), поэтому такого варианта нет
  const AL: [string, string][] = [['', 'Авто по СТО'], ['left', 'Влево'], ['center', 'По центру']];
  return (
    <Modal title="Свойства таблицы" testid="tfc-table-props" onClose={() => close()} width={620}
      footer={<><Btn label="Отмена" onClick={() => close()} /><Btn label="Готово" primary onClick={ok} testid="tfc-props-ok" /></>}>
      <div className="form-grid">
        <Field label="Строк головки" hint="повторяется на каждой странице при переносе таблицы (4.9.11)">
          <select value={head} onChange={(e) => setHead(+e.target.value)} data-testid="tfc-props-head">{[1, 2, 3, 4].filter((n) => n < t0.map.height || n === 1).map((n) => <option key={n} value={n}>{n}</option>)}</select>
        </Field>
        <Field label="Ширины граф">
          <select value={mode} onChange={(e) => setMode(e.target.value as any)} data-testid="tfc-props-mode">
            <option value="auto">По содержимому (как writer)</option><option value="manual">Заданные, см</option>
          </select>
        </Field>
      </div>
      <table className="tfc-props-table">
        <thead><tr><th>Графа</th><th>Ширина, см</th><th>Выравнивание значений</th></tr></thead>
        <tbody>
          {cm.map((w, i) => (
            <tr key={i}>
              <td>{i + 1}{i === 0 ? ' (боковик)' : ''}</td>
              <td><input type="number" step={0.1} min={0.5} max={TEXT_W_CM} value={w} disabled={mode === 'auto'} data-testid={`tfc-w-${i}`}
                onChange={(e) => { const v = [...cm]; v[i] = +e.target.value; setCm(v); }} /></td>
              <td><select value={aligns[i] ?? ''} onChange={(e) => { const v = [...aligns]; v[i] = (e.target.value || null) as Align; setAligns(v); }}>
                {AL.map(([v, tt]) => <option key={v} value={v}>{v ? tt : `${tt} (${defs[i] === 'left' ? 'влево' : 'по центру'})`}</option>)}
              </select></td>
            </tr>
          ))}
        </tbody>
      </table>
      {mode === 'manual' && (
        <div className="row tfc-props-row">
          <span className={Math.abs(sum - TEXT_W_CM) > 0.05 ? 'tfc-warn' : 'muted'}>Сумма: {sum.toFixed(1).replace('.', ',')} см из {TEXT_W_CM} (таблица всегда во всю ширину поля; при сохранении ширины пропорционально подгоняются)</span>
          <Btn label="Равные" onClick={() => setCm(cm.map(() => Math.round((TEXT_W_CM / cm.length) * 10) / 10))} />
        </div>
      )}
      <p className="tfc-note">По СТО (4.9.9, 4.9.23): головка - по центру; боковик - влево; значения - по центру графы, прочерк - по центру.
        Длинный текст (в среднем более 40 знаков) - влево.</p>
    </Modal>
  );
}

function colAlign(t: ReturnType<typeof tableCtx>, c: number): Align {
  const vals = new Set(cells(t!).filter((x) => x.col === c && x.row >= t!.headerRows).map((x) => x.node.attrs.align ?? null));
  return vals.size === 1 ? ([...vals][0] as Align) : null;
}

function measured(ed: any, t: NonNullable<ReturnType<typeof tableCtx>>): number[] {
  const dom = ed.view.nodeDOM(t.tablePos) as HTMLElement | null;
  const table = (dom?.querySelector?.('table') || (dom?.tagName === 'TABLE' ? dom : null)) as HTMLTableElement | null;
  if (!table) return normalizeWidths(Array(t.map.width).fill(1));
  const ws: number[] = [];
  const all = Array.from(table.rows);
  for (let c = 0; c < t.map.width; c++) {
    let w = 0;
    for (const r of all) {
      let col = 0;
      for (const cell of Array.from(r.cells)) {
        if (col === c && cell.colSpan === 1) { w = cell.getBoundingClientRect().width; break; }
        col += cell.colSpan;
      }
      if (w) break;
    }
    ws.push(w || TEXT_W_PX / t.map.width);
  }
  return normalizeWidths(ws);
}

export { stoTableJson };
